import { useClarityWorkflow } from "api/clarityApi"
import { useUpdateCommentClarificationMutation } from "api/collaborateApi"
import { Check, Lightbulb, PlusCircle } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { Button } from "ui"
import { cn } from "utils/cn"
import { useAppSelector } from "../../../../store"
import { getUser } from "../../../../store/userSlice"

interface Option {
    id: number
    text: string
}

interface MultiSelectToggleProps {
    commentId: string
    feedback?: string
    options: Option[]
    defaultSelected?: number[]
}

export default function ClaritySelectToggle({
    commentId,
    feedback,
    options,
    defaultSelected = [],
}: MultiSelectToggleProps) {
    const [selectedItems, setSelectedItems] = useState<number[]>(defaultSelected)
    const {
        completeClarification,
        isLoading,
        hasError
    } = useClarityWorkflow();
    const [dismiss, { isLoading: isDismissLoading }] = useUpdateCommentClarificationMutation()
    const userData = useAppSelector(getUser);

    const toggleItem = (itemId: number) => {
        const newSelection = selectedItems.includes(itemId)
            ? selectedItems.filter((id) => id !== itemId)
            : [...selectedItems, itemId]

        setSelectedItems(newSelection)
    }

    const isSelected = (itemId: number) => selectedItems.includes(itemId)

    const handleClarify = async () => {
        if (isLoading || hasError) return;

        if (selectedItems.length === 0) {
            toast.error("Please select at least one option and ensure feedback is provided.");
            return;
        }

        if (!feedback || feedback.trim() === "") {
            toast.error("Feedback is required to clarify.");
            return;
        }

        try {
            await completeClarification({
                commentId,
                selectedOptionIds: selectedItems,
                originalFeedback: feedback,
                options,
                userName: userData?.name
            });
            setSelectedItems([]);
        } catch (error) {
            console.error("Clarification failed:", error);
        }
    }

    return (
        <div className="w-full max-w-md mx-auto">
            <div className="flex flex-col gap-2">
                {options.map((option) => (
                    <button
                        key={option.id}
                        onClick={() => toggleItem(option.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg border transition-all  flex items-center justify-between 
                            ${isSelected(option.id)
                                ? "border-ata-purple text-ata-purple dark:bg-tertiary/5"
                                : "border-border text-textcolor hover:bg-gray-50 dark:hover:bg-tertiary"
                            }`}
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <Lightbulb className={cn(isSelected(option.id) ? "text-ata-purple" : "text-textcolor", "flex-shrink-0 self-start mt-0.5")} size={16} />
                            <span className="text-sm flex-1 min-w-0 leading-tight text-textcolor" dangerouslySetInnerHTML={{ __html: option.text }}></span>
                            <div className="h-4 w-4">
                                {isSelected(option.id) && (
                                    <Check className="text-ata-purple flex-shrink-0" size={16} />
                                )}
                            </div>
                        </div>
                    </button>
                ))}
            </div>

            <div className="flex gap-3 pt-3">
                <Button variant="secondary"
                    size="sm"
                    className="flex-1"
                    loading={isDismissLoading}
                    onClick={() => {
                        setSelectedItems([]);
                        dismiss({ status: "dismissed", commentId });
                    }}
                >
                    Dismiss
                </Button>
                <Button
                    onClick={handleClarify}
                    size="sm"
                    loading={isLoading || isDismissLoading}
                    disabled={selectedItems.length === 0}
                    variant="muted" className="flex-1" leftIcon={<PlusCircle size={16} />}>
                    Clarify
                </Button>
            </div>
        </div >
    )
}
